import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo } from "react";
import { Input } from "@fluentui/react-components";
import { LocationRegular, SearchRegular } from "@fluentui/react-icons";
import { useLieu } from "../../context/LieuContext";
import { useAuth } from "../../context/AuthContext";
import { getAllApprobateurs } from "../../services/dataService";
const LIEU_ORDER = ["Siège", "CFRP"];
function categorize(lieu) {
    if (lieu === "Siège" || lieu === "CFRP")
        return lieu;
    if (lieu.toLowerCase().includes("comité") || lieu.toLowerCase().includes("comite"))
        return "Comités";
    if (lieu.toLowerCase().includes("établissement") || lieu.toLowerCase().includes("etablissement"))
        return "Établissements";
    return "Autres";
}
function groupLieux(lieux) {
    const groups = {};
    for (const l of lieux) {
        const cat = categorize(l);
        if (!groups[cat])
            groups[cat] = [];
        groups[cat].push(l);
    }
    // Sort within groups
    for (const cat of Object.keys(groups)) {
        groups[cat].sort((a, b) => a.localeCompare(b));
    }
    return groups;
}
const GROUP_ORDER = ["Siège", "CFRP", "Établissements", "Comités", "Autres"];
export const LieuSelector = () => {
    const { selectedLieu, setSelectedLieu } = useLieu();
    const { apiToken } = useAuth();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [lieux, setLieux] = useState([]);
    const containerRef = useRef(null);
    useEffect(() => {
        if (!apiToken)
            return;
        getAllApprobateurs(apiToken).then((approbs) => {
            const set = new Set();
            for (const a of approbs) {
                const l = (a.lieu || "").trim();
                if (l)
                    set.add(l);
            }
            setLieux(Array.from(set));
        }).catch(() => { });
    }, [apiToken]);
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);
    const filtered = useMemo(() => {
        if (!search)
            return lieux;
        const s = search.toLowerCase();
        return lieux.filter((l) => l.toLowerCase().includes(s));
    }, [lieux, search]);
    const groups = useMemo(() => groupLieux(filtered), [filtered]);
    const select = (lieu) => {
        setSelectedLieu(lieu);
        setOpen(false);
        setSearch("");
    };
    return (_jsxs("div", { ref: containerRef, style: { position: "relative" }, children: [_jsxs("button", { onClick: () => setOpen((o) => !o), style: {
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "16px",
                    background: "#f0f4ff",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#0066cc",
                    whiteSpace: "nowrap",
                }, title: "Changer de lieu", children: [_jsx(LocationRegular, { style: { fontSize: 14 } }), selectedLieu, _jsx("span", { style: { fontSize: 10, color: "#888" }, children: "\u25BC" })] }), open && (_jsxs("div", { style: {
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    right: 0,
                    zIndex: 200,
                    background: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                    minWidth: "260px",
                    maxHeight: "380px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }, children: [_jsx("div", { style: { padding: "10px 12px", borderBottom: "1px solid #f3f4f6" }, children: _jsx(Input, { contentBefore: _jsx(SearchRegular, {}), placeholder: "Rechercher un lieu...", value: search, onChange: (_, d) => setSearch(d.value), size: "small", style: { width: "100%" }, autoFocus: true }) }), _jsxs("div", { style: { overflowY: "auto", maxHeight: "320px" }, children: [GROUP_ORDER.filter((g) => groups[g]?.length > 0).map((groupName) => (_jsxs("div", { children: [_jsx("div", { style: {
                                            padding: "6px 12px 2px",
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            color: "#999",
                                            letterSpacing: "0.5px",
                                        }, children: groupName }), groups[groupName].map((lieu) => (_jsxs("div", { onClick: () => select(lieu), style: {
                                            padding: "7px 16px",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            background: lieu === selectedLieu ? "#f0f4ff" : "transparent",
                                            color: lieu === selectedLieu ? "#0066cc" : "#374151",
                                            fontWeight: lieu === selectedLieu ? 600 : 400,
                                        }, onMouseEnter: (ev) => {
                                            if (lieu !== selectedLieu)
                                                ev.currentTarget.style.background = "#f9fafb";
                                        }, onMouseLeave: (ev) => {
                                            ev.currentTarget.style.background =
                                                lieu === selectedLieu ? "#f0f4ff" : "transparent";
                                        }, children: [lieu === selectedLieu && (_jsx("span", { style: { color: "#0066cc", fontSize: 12 }, children: "\u2713" })), lieu !== selectedLieu && _jsx("span", { style: { width: 12 } }), lieu] }, lieu)))] }, groupName))), filtered.length === 0 && (_jsx("div", { style: { padding: "16px", textAlign: "center", color: "#999", fontSize: "13px" }, children: "Aucun lieu trouv\u00E9" }))] })] }))] }));
};
